/**
 * LoeschBestaetigungDialog — Zwei-Stufen-Bestaetigung fuers endgueltige Loeschen
 * (Plan 03-04, ACT-02). Gleiche Grundstruktur wie WhitelistDialog/SnoozeDialog, aber
 * bewusst deutlich alarmierender: rote Umrandung/Text, Bestaetigen-Button erst
 * klickbar sobald exakt das Wort LÖSCHEN eingetippt wurde. Der eingetippte String
 * wird 1:1 als `bestaetigung` an den Backend-Endpunkt gesendet — das Backend prueft
 * ihn dort ERNEUT (istLoeschBestaetigungGueltig), das Frontend-Gate ist nur UX.
 */
import { useEffect, useState } from 'react';
import type { ChangeEvent } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@libero/core/components/ui/dialog';
import { Button } from '@libero/core/components/ui/button';
import { Input } from '@libero/core/components/ui/input';

interface LoeschBestaetigungDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  anzahl: number;
  onConfirm: (bestaetigung: string) => Promise<void> | void;
}

export default function LoeschBestaetigungDialog({
  open,
  onOpenChange,
  anzahl,
  onConfirm,
}: LoeschBestaetigungDialogProps) {
  const [eingabe, setEingabe] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setEingabe('');
      setError(null);
    }
  }, [open]);

  // Bestaetigen bleibt disabled, bis exakt das Pflichtwort eingetippt wurde.
  const nichtBestaetigt = eingabe.trim() !== 'LÖSCHEN';
  const bestaetigt = !nichtBestaetigt;

  async function handleConfirm() {
    if (!bestaetigt) return;
    setSaving(true);
    setError(null);
    try {
      await onConfirm(eingabe.trim());
      setEingabe('');
      onOpenChange(false);
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || err?.message || 'Löschen konnte nicht gestartet werden.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-2 border-red-600">
        <DialogHeader>
          <DialogTitle className="text-red-700">
            {anzahl === 1 ? 'Artikel endgültig löschen' : `${anzahl} Artikel endgültig löschen`}
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-red-700 font-semibold">
          Diese Aktion löscht {anzahl} Artikel UNWIDERRUFLICH aus Plenty. Es gibt kein Zurück — Plenty kennt keine
          Wiederherstellung gelöschter Artikel.
        </p>

        <div className="space-y-1">
          <label htmlFor="loesch-bestaetigung" className="text-xs font-bold text-gray-600 uppercase tracking-wide">
            Zum Bestätigen "LÖSCHEN" eintippen
          </label>
          <Input
            id="loesch-bestaetigung"
            value={eingabe}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setEingabe(e.target.value)}
            placeholder="LÖSCHEN"
            autoFocus
            className="border-red-300 focus-visible:ring-red-500"
          />
        </div>

        {error && <p className="text-sm text-red-700">{error}</p>}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Abbrechen
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={saving || !bestaetigt}
            className="bg-red-600 text-white hover:bg-red-700"
          >
            {saving ? 'Wird gestartet…' : 'Endgültig löschen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
